import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { IApiEpOrganizationRoleCodename } from "@smp/api";
import { UiSvgIcon, UiTooltip } from "@smp/ui";
import {
  IconBriefcase,
  IconCrown,
  IconEye,
  IconPencil,
} from "@tabler/icons-react";
import { ReactElement, useCallback, useMemo } from "react";

import { epOrganizationRoleNameByCodenameGet } from "@/utils";

import { IProps } from "./organization_role_icon.types";

const isValidCodename = (
  code: string,
): code is IApiEpOrganizationRoleCodename =>
  ["editor_v1", "empty_v1", "manager_v1", "observer_v1"].includes(code);

const _getCodenamePrefix = (
  codename: IApiEpOrganizationRoleCodename | undefined,
): string => {
  let codenamePrefix;
  if (codename !== undefined) {
    const codenameClean: IApiEpOrganizationRoleCodename = codename;
    const regex = /^(\w+)_v[0-9]+$/i;
    const found = codenameClean.match(regex);
    codenamePrefix = found && found[1];
  }
  return codenamePrefix || "owner";
};

export const OrganizationRoleIcon = ({
  className,
  codename: codenameString,
  tooltip,
}: IProps) => {
  const { i18n } = useLingui();

  const codename = useMemo(
    () =>
      codenameString && isValidCodename(codenameString)
        ? codenameString
        : undefined,
    [codenameString],
  );

  const getIcon = useCallback(
    (codename: IProps["codename"]): ReactElement => {
      let codenamePrefix = _getCodenamePrefix(
        codename as IApiEpOrganizationRoleCodename,
      );
      switch (codenamePrefix) {
        case "empty":
          return (
            <UiSvgIcon className={className} viewBox="0 0 24 24">
              <path d="M15.07,11.25L14.17,12.17C13.45,12.89 13,13.5 13,15H11V14.5C11,13.39 11.45,12.39 12.17,11.67L13.41,10.41C13.78,10.05 14,9.55 14,9C14,7.89 13.1,7 12,7A2,2 0 0,0 10,9H8A4,4 0 0,1 12,5A4,4 0 0,1 16,9C16,9.88 15.64,10.67 15.07,11.25M13,19H11V17H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12C22,6.47 17.5,2 12,2Z" />
            </UiSvgIcon>
          );
        case "observer":
          return <IconEye />;
        case "editor":
          return <IconPencil />;
        case "manager":
          return <IconBriefcase />;
        default:
          // owner
          return <IconCrown />;
      }
    },
    [className],
  );

  return useMemo(() => {
    const roleIcon = getIcon(codename);
    if (tooltip) {
      const roleIcon = getIcon(codename);
      const tooltipTitle = codename
        ? epOrganizationRoleNameByCodenameGet(codename)
        : i18n._(t`Owner`);

      return <UiTooltip title={tooltipTitle}>{roleIcon}</UiTooltip>;
    } else {
      return roleIcon;
    }
  }, [codename, getIcon, i18n, tooltip]);
};
